/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../../core/viewmodel/ViewModel';
import { ModuleEditingModel } from '../../../../../models/components/ModuleEditingModel';
import { StoreProvider } from '../../../../../stores/StoreProvider';
import * as H from 'history';
import { SnackbarStore } from '../../../../../stores/application/SnackbarStore';
import { AuthStore } from '../../../../../stores/application/AuthStore';
import { useParams } from 'react-router-dom';
import { ModuleService } from '../../../../../services/ModuleService';
import { CourseService } from '../../../../../services/CourseService';
import { AddModuleDialogStore } from '../../../../../stores/components/AddModuleDialogStore';
import { MaterialCircularProgress } from '../../../../../models/material/MaterialCircularProgress';
import { ModulePackage } from '../../../../../types/course/ModulePackage';

export class AddModuleDialogViewModel extends ViewModel {
	private readonly circularProgress = new MaterialCircularProgress();
	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly addModuleDialogStore: AddModuleDialogStore;

	private moduleEditingModel: ModuleEditingModel = new ModuleEditingModel();
	private routerParams: { courseId: string } = useParams();
	private moduleService: ModuleService;
	private courseService: CourseService;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.authStore = storeProvider.authStore;
		this.moduleService = storeProvider.moduleService;
		this.courseService = storeProvider.courseService;
		this.addModuleDialogStore = storeProvider.addModuleDialogStore;
		this.queryAllModulePackages();
	}

	public isLoading(): boolean {
		return this.circularProgress.isLoading;
	}

	public isAddModuleDialogOpen(): boolean {
		return this.addModuleDialogStore.isAddModuleDialogOpen();
	}

	public closeAddModuleDialog(): void {
		return this.addModuleDialogStore.closeAddModuleDialog();
	}

	public getOpenModulePackageId(): string | undefined {
		return this.moduleEditingModel.openModulePackageId;
	}

	public setOpenModulePackageId(openModulePackageId: string | undefined): void {
		this.moduleEditingModel.setOpenModulePackageId(openModulePackageId);
	}

	public isCurrentModulePackage(modulePackageId: string): boolean {
		return this.moduleEditingModel.openModulePackageId === modulePackageId;
	}

	public setAllModulePackages(modulePackages: ModulePackage[]): void {
		return this.moduleEditingModel.setAllModulePackages(modulePackages);
	}

	public getAllModulePackages(): ModulePackage[] {
		return this.moduleEditingModel.allModulePackages;
	}

	public getCourseModulePackages(): ModulePackage[] {
		return this.addModuleDialogStore.getCourseModulePackages();
	}

	public isModulePackageInCourse(modulePackageId: string): boolean {
		return this.getCourseModulePackages().some((modulePackage: ModulePackage) => modulePackage.id === modulePackageId);
	}

	public queryAllModulePackages(): Promise<void> | void {
		this.circularProgress.setIsLoading(true);
		const authToken: string = this.authStore.getAuthToken();

		return this.moduleService
			.getModulePackages(authToken)
			.then((modulePackages: ModulePackage[]) => {
				this.setAllModulePackages(modulePackages);
				this.circularProgress.setIsLoading(false);
			})
			.catch(() => {
				this.snackbarStore.showError('modules.query_error');
			});
	}

	public addModulePackage(modulePackageId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();

		return this.courseService
			.addCourseModulePackage(this.routerParams.courseId, modulePackageId, authToken)
			.then(() => {
				this.queryCourseModulePackages(this.routerParams.courseId);
				this.snackbarStore.showSuccess('modules.module_editing.module_added');
			})
			.catch(() => {
				this.snackbarStore.showError('modules.module_editing.module_added_error');
			});
	}

	public queryCourseModulePackages(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();

		return this.courseService
			.getCourseModulePackages(authToken, courseId)
			.then((modulePackages: ModulePackage[]) => {
				this.addModuleDialogStore.setCourseModulePackages(modulePackages);
			})
			.catch(() => {
				this.snackbarStore.showError('modules.query_error');
			});
	}
}
